import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { sumsubHub } from "@/lib/api";
import { AnswerPill, AuthImg, LabelPill, StatusPill, fmtDate, fmtTs, nice } from "./sumsubShared";
import { SumsubDocs } from "./SumsubDocs";
import { ActionBar, ActionDialog } from "./SumsubActions";

const Row = ({ k, v, mono = false, testId }) => (
  <div className="flex gap-2 py-0.5 text-xs" data-testid={testId}>
    <span className="w-32 shrink-0 text-slate-500">{k}</span>
    <span className={`min-w-0 break-words text-slate-900 ${mono ? "font-mono" : ""}`}>{v || <span className="text-slate-300">—</span>}</span>
  </div>
);
const Card = ({ title, children, testId, right }) => (
  <section className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid={testId}>
    <header className="mb-2 flex items-center justify-between"><h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</h3>{right}</header>
    {children}
  </section>
);

function Overview({ d }) {
  const a = d.applicant, r = d.review, rr = r.reviewResult || {};
  const addr = a.addresses?.[0] || {};
  const risk = Object.entries(d.risk_labels || {}).filter(([, v]) => Array.isArray(v));
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="sumsub-overview">
      <Card title="Review" testId="sumsub-review-card">
        <div className="mb-2 flex flex-wrap items-center gap-1.5"><StatusPill status={r.reviewStatus} /><AnswerPill answer={rr.reviewAnswer} />{rr.reviewRejectType && <LabelPill tone={rr.reviewRejectType === "FINAL" ? "red" : "amber"}>{rr.reviewRejectType}</LabelPill>}</div>
        {rr.rejectLabels?.length > 0 && <div className="mb-2 flex flex-wrap gap-1">{rr.rejectLabels.map((l) => <LabelPill key={l} tone="red">{l}</LabelPill>)}</div>}
        <Row k="Level" v={r.levelName} />
        <Row k="Attempts" v={r.attemptCnt != null ? String(r.attemptCnt) : null} />
        <Row k="Review started" v={fmtTs(r.createDate)} />
        <Row k="Reviewed" v={fmtTs(r.reviewDate)} />
        <Row k="Moderator comment" v={rr.moderationComment} />
        <Row k="Internal comment" v={rr.clientComment} />
      </Card>
      <Card title="Personal data" testId="sumsub-personal-card">
        <Row k="Name" v={[a.first_name, a.last_name].filter(Boolean).join(" ")} />
        <Row k="Date of birth" v={fmtDate(a.dob)} />
        <Row k="Country" v={[a.country, a.nationality && `nationality ${a.nationality}`].filter(Boolean).join(" · ")} />
        <Row k="Email" v={a.email} mono />
        <Row k="Phone" v={a.phone} mono />
        <Row k="Address" v={addr.formattedAddress || [addr.street, addr.town, addr.postCode].filter(Boolean).join(", ")} />
        <Row k="Platform" v={[a.platform, a.ip_country && `IP ${a.ip_country}`, a.lang].filter(Boolean).join(" · ")} />
        <Row k="Agreement" v={a.agreement?.acceptedAt ? `accepted ${fmtTs(a.agreement.acceptedAt)} (${a.agreement.source})` : null} />
      </Card>
      <Card title="Identity document (extracted)" testId="sumsub-iddoc-card">
        {a.id_docs?.length ? a.id_docs.map((doc, i) => (
          <div key={i} className={i ? "mt-2 border-t border-slate-100 pt-2" : ""}>
            <Row k="Type" v={[nice(doc.idDocType), doc.country, doc.ocrDocType].filter(Boolean).join(" · ")} />
            <Row k="Name on doc" v={[doc.firstNameEn || doc.firstName, doc.lastNameEn || doc.lastName].filter(Boolean).join(" ")} />
            <Row k="Number" v={doc.number} mono />
            <Row k="DOB on doc" v={fmtDate(doc.dob)} />
            <Row k="Valid until" v={doc.validUntil ? fmtDate(doc.validUntil) : doc.termless ? "no expiry" : null} />
          </div>
        )) : <p className="text-xs text-slate-400">Nothing extracted yet.</p>}
      </Card>
      <Card title="Application fields" testId="sumsub-custom-card">
        {a.custom_fields?.length ? a.custom_fields.map((m) => <Row key={m.key} k={m.key} v={m.value} />) : <p className="text-xs text-slate-400">No custom fields.</p>}
        <Row k="External user ID" v={a.external_user_id} mono />
        <Row k="Applicant ID" v={a.id} mono />
        <Row k="Created" v={`${fmtTs(a.created_at)}${a.created_by ? ` · ${a.created_by}` : ""}`} />
      </Card>
      <Card title="Risk labels & AML" testId="sumsub-risk-card">
        {risk.length ? risk.map(([grp, vals]) => <Row key={grp} k={nice(grp)} v={<span className="flex flex-wrap gap-1">{vals.map((v) => <LabelPill key={v} tone="amber">{v}</LabelPill>)}</span>} />) : <p className="text-xs text-slate-400">No risk labels.</p>}
        <div className="mt-2 border-t border-slate-100 pt-2">
          {d.aml ? (<>
            <Row k="AML answer" v={<AnswerPill answer={d.aml.review?.reviewAnswer} />} />
            <Row k="Hits" v={String((d.aml.hits || []).length)} />
            {(d.aml.hits || []).slice(0, 6).map((h, i) => <Row key={i} k={h.matchStatus || "hit"} v={[h.name || h.entityName, (h.types || h.matchTypes || []).join(", ")].filter(Boolean).join(" · ")} />)}
          </>) : <p className="text-xs text-slate-400">No AML case on this applicant.</p>}
        </div>
        {d.similar?.length > 0 && <Row k="Similar applicants" v={d.similar.map((s) => s.applicantId || s.id || JSON.stringify(s)).join(", ")} mono />}
      </Card>
      <Card title="Tags & notes" testId="sumsub-tags-card">
        <div className="mb-2 flex flex-wrap gap-1">{a.tags?.length ? a.tags.map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>) : <span className="text-xs text-slate-400">No tags</span>}</div>
        {d.notes?.length ? d.notes.map((n, i) => <div key={n.id || i} className="border-t border-slate-100 py-1 text-xs"><span className="text-slate-900">{n.note || n.text}</span><span className="ml-2 text-[10px] text-slate-400">{fmtTs(n.createdAt || n.ts)}{n.createdBy ? ` · ${n.createdBy}` : ""}</span></div>) : <p className="text-xs text-slate-400">No notes.</p>}
      </Card>
    </div>
  );
}

function Steps({ d }) {
  const a = d.applicant;
  return (
    <div className="space-y-3" data-testid="sumsub-steps">
      <div className="grid gap-2 md:grid-cols-2">
        {d.steps.map((s) => (
          <div key={s.key} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid={`sumsub-step-${s.key}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium text-slate-900">{s.label}{s.required && !s.present ? <span className="ml-1 text-[10px] text-slate-400">not started</span> : null}</div>
              <AnswerPill answer={s.answer} testId={`sumsub-step-answer-${s.key}`} />
            </div>
            <div className="mt-0.5 text-[11px] text-slate-500">{[s.doc_type && nice(s.doc_type), s.country, s.doc_types?.length ? `accepts ${s.doc_types.map(nice).join(", ")}` : null].filter(Boolean).join(" · ")}</div>
            {s.reject_labels?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{s.reject_labels.map((l) => <LabelPill key={l} tone="red">{l}</LabelPill>)}{s.reject_type && <LabelPill tone="amber">{s.reject_type}</LabelPill>}</div>}
            {s.moderation_comment && <p className="mt-1 text-xs text-slate-700">“{s.moderation_comment}”</p>}
            {s.image_ids?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {s.image_ids.map((id) => (
                  <div key={id} className="relative h-16 w-24 overflow-hidden rounded border bg-slate-100">
                    <AuthImg applicantId={a.id} imageId={id} inspectionId={a.inspection_id} preview className="h-full w-full object-cover" />
                    <div className="absolute bottom-0.5 left-0.5"><AnswerPill answer={s.image_results?.[id]} /></div>
                  </div>
                ))}
              </div>
            )}
            {s.custom_fields?.length > 0 && <div className="mt-1 text-[10px] text-slate-400">Fields: {s.custom_fields.map((f) => f.displayName || f.name).join(", ")}</div>}
          </div>
        ))}
      </div>
      <Card title={`Documents · ${d.documents.length}`} testId="sumsub-docs-card">
        <SumsubDocs applicantId={a.id} inspectionId={a.inspection_id} documents={d.documents} />
      </Card>
    </div>
  );
}

const ACT_TONE = (act = "") => act.startsWith("moderator") ? "bg-violet-500" : act.startsWith("user") ? "bg-sky-500" : act.includes("reset") || act.includes("removed") ? "bg-amber-500" : "bg-slate-300";

function Timeline({ d }) {
  return (
    <div className="grid gap-3 lg:grid-cols-3" data-testid="sumsub-timeline">
      <Card title="Review history" testId="sumsub-history-card">
        {d.history.length ? d.history.map((h, i) => (
          <div key={i} className="border-b border-slate-100 py-1.5 text-xs last:border-0">
            <div className="flex items-center gap-1.5"><AnswerPill answer={h.reviewResult?.reviewAnswer} /><StatusPill status={h.reviewStatus} /><span className="font-mono text-[10px] text-slate-400">{fmtTs(h.reviewDate)}</span></div>
            {h.reviewResult?.rejectLabels?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{h.reviewResult.rejectLabels.map((l) => <LabelPill key={l} tone="red">{l}</LabelPill>)}{h.reviewResult.reviewRejectType && <LabelPill tone="amber">{h.reviewResult.reviewRejectType}</LabelPill>}</div>}
            {h.reviewResult?.moderationComment && <p className="mt-0.5 text-slate-600">“{h.reviewResult.moderationComment}”</p>}
          </div>
        )) : <p className="text-xs text-slate-400">No reviews yet.</p>}
      </Card>
      <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Activity · {d.timeline.length}</h3>
        <ol className="relative ml-2 border-l border-slate-200">
          {d.timeline.map((t, i) => (
            <li key={i} className="mb-3 ml-4 text-xs" data-testid={`sumsub-tl-${i}`}>
              <span className={`absolute -left-[5px] mt-1 h-2.5 w-2.5 rounded-full ${ACT_TONE(t.activity)}`} />
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="font-medium text-slate-900">{nice((t.activity || "").split(":").slice(1).join(" "))}</span>
                <span className="font-mono text-[10px] text-slate-400">{fmtTs(t.ts)}</span>
                {t.step && <LabelPill>{t.step}</LabelPill>}{t.doc_type && <LabelPill>{t.doc_type}</LabelPill>}
                {t.answer && t.activity?.includes("review") && <AnswerPill answer={t.answer} />}
              </div>
              <div className="text-[11px] text-slate-500">
                {[t.subject && t.subject !== "Service" && !t.subject.includes("_") ? `by ${t.subject}` : null, t.device, t.ip && `${t.ip}${t.ip_city ? ` · ${t.ip_city}` : ""}${t.risky_asn ? " · risky ASN" : ""}`].filter(Boolean).join(" · ")}
              </div>
              {t.labels?.length > 0 && t.activity?.includes("review") && <div className="mt-0.5 flex flex-wrap gap-1">{t.labels.map((l) => <LabelPill key={l} tone="red">{l}</LabelPill>)}</div>}
              {t.note && <p className="mt-0.5 text-slate-700">“{t.note}”</p>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function PortalLog({ items }) {
  return (
    <Card title={`Portal actions · ${items.length}`} testId="sumsub-portal-log">
      {items.length ? (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase text-slate-500"><tr><th className="py-1 text-left">When</th><th className="py-1 text-left">Who</th><th className="py-1 text-left">Action</th><th className="py-1 text-left">Details</th><th className="py-1 text-left">Result</th></tr></thead>
          <tbody>
            {items.map((x, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`sumsub-portal-log-row-${i}`}>
                <td className="py-1 font-mono text-[10px] text-slate-500">{fmtTs(x.at)}</td><td className="py-1">{x.actor}</td><td className="py-1 font-medium">{nice(x.action)}</td>
                <td className="py-1 text-slate-600">{Object.entries(x.params || {}).filter(([, v]) => v && (!Array.isArray(v) || v.length)).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}</td>
                <td className="py-1">{x.ok ? <LabelPill>ok</LabelPill> : <LabelPill tone="red">{x.error || "failed"}</LabelPill>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="text-xs text-slate-400">Nothing done from the portal yet.</p>}
    </Card>
  );
}

export function SumsubDetail({ applicantId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [action, setAction] = useState(null);
  const load = async () => {
    setErr(null);
    try { setD(await sumsubHub.detail(applicantId)); } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { setD(null); if (applicantId) load(); }, [applicantId]); // eslint-disable-line react-hooks/exhaustive-deps
  const a = d?.applicant, rr = d?.review?.reviewResult || {};
  return (
    <Sheet open={!!applicantId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[min(1180px,96vw)] sm:max-w-none overflow-y-auto p-0" data-testid="sumsub-detail-sheet">
        <SheetHeader className="sticky top-0 z-20 border-b bg-white/95 px-5 py-3 backdrop-blur">
          {d ? (
            <div className="space-y-2">
              <SheetTitle className="flex flex-wrap items-center gap-2 text-lg">
                <span data-testid="sumsub-detail-name">{[a.first_name, a.last_name].filter(Boolean).join(" ") || "Unnamed applicant"}</span>
                <StatusPill status={d.review.reviewStatus} testId="sumsub-detail-status" /><AnswerPill answer={rr.reviewAnswer} testId="sumsub-detail-answer" />
                {rr.reviewRejectType && <LabelPill tone={rr.reviewRejectType === "FINAL" ? "red" : "amber"}>{rr.reviewRejectType}</LabelPill>}
                <span className="text-xs font-normal text-slate-500">{d.review.levelName} · created {fmtTs(a.created_at)}</span>
                <span className="ml-auto flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={load} data-testid="sumsub-detail-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
                  <a href={d.cockpit_url} target="_blank" rel="noreferrer" data-testid="sumsub-detail-cockpit"><Button variant="outline" size="sm" className="h-7 text-xs"><ExternalLink className="w-3.5 h-3.5 mr-1" />Open in Sumsub</Button></a>
                </span>
              </SheetTitle>
              <ActionBar canAct={!!d.can_act} onPick={setAction} />
            </div>
          ) : <SheetTitle className="text-base">{err ? <span className="text-rose-700" data-testid="sumsub-detail-error">{err}</span> : <span className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Loading applicant…</span>}</SheetTitle>}
        </SheetHeader>
        {d && (
          <Tabs defaultValue="overview" className="px-5 py-4">
            <TabsList className="mb-3">
              <TabsTrigger value="overview" data-testid="sumsub-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="steps" data-testid="sumsub-tab-steps">Steps &amp; documents <Badge variant="outline" className="ml-1.5 text-[9px]">{d.documents.length}</Badge></TabsTrigger>
              <TabsTrigger value="timeline" data-testid="sumsub-tab-timeline">Timeline <Badge variant="outline" className="ml-1.5 text-[9px]">{d.timeline.length}</Badge></TabsTrigger>
              <TabsTrigger value="log" data-testid="sumsub-tab-log">Portal log{d.portal_actions.length ? <Badge variant="outline" className="ml-1.5 text-[9px]">{d.portal_actions.length}</Badge> : null}</TabsTrigger>
            </TabsList>
            <TabsContent value="overview"><Overview d={d} /></TabsContent>
            <TabsContent value="steps"><Steps d={d} /></TabsContent>
            <TabsContent value="timeline"><Timeline d={d} /></TabsContent>
            <TabsContent value="log"><PortalLog items={d.portal_actions} /></TabsContent>
          </Tabs>
        )}
        {action && d && (
          <ActionDialog kind={action} applicant={a} steps={d.steps} onClose={() => setAction(null)}
            onDone={() => { load(); onChanged?.(); toast.message("Sumsub may take a few seconds to reflect the change — refresh if needed"); }} />
        )}
      </SheetContent>
    </Sheet>
  );
}
